/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import {
  restoreElasticsearchProject,
  restoreObservabilityProject,
  restoreSecurityProject,
} from '@modules/project-admin-api/v1/callers'
import type { ProjectID } from '@modules/project-admin-api/v1/types'
import { queryClient } from '@modules/query'
import type { ProjectType } from '@modules/ui-types/projects'

import { makeQueryKey } from './helpers'

import type { UseMutationResult } from 'react-query'

const restoreCallers = {
  elasticsearch: restoreElasticsearchProject,
  observability: restoreObservabilityProject,
  security: restoreSecurityProject,
}

export function useRestoreProjectMutation<T extends ProjectType>(
  projectType: T,
): UseMutationResult<void, unknown, { id: ProjectID }> {
  const restoreCaller = restoreCallers[projectType]

  return useMutation({
    mutationFn: ({ id }: Parameters<typeof restoreCaller>[0]['pathParameters']) =>
      restoreCaller({
        pathParameters: { id },
      }),
    onSuccess: (_, { id }) => onProjectRestored(projectType, id),
  })
}

const onProjectRestored = (type: ProjectType, id: ProjectID) => {
  const listQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'listElasticsearchProjects',
        observability: 'listObservabilityProjects',
        security: 'listSecurityProjects',
      } as const
    )[type],
  )
  queryClient.invalidateQueries(listQueryKey)

  const detailQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'getElasticsearchProject',
        observability: 'getObservabilityProject',
        security: 'getSecurityProject',
      } as const
    )[type],
    id,
  )
  queryClient.invalidateQueries(detailQueryKey)
}
